import type { MUIDataTableOptions } from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import ResultsSummary from '@components/ResultsSummary';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import { useNavigate } from '@hooks/useNavigate';

import { formatDate, formatName } from '@utils/format';

import type { ListAccountsQuery } from 'generated/account.generated';
import { AccountType } from 'generated/types';

interface AccountListProps {
  parent_id?: string;
  data: ListAccountsQuery['listAccounts'];
  /**
   * Type of the currently active tab, for the "Showing X of Y" summary line.
   * Omit for contexts with no tab concept (e.g. an org's child accounts) —
   * the summary line is skipped when this isn't provided.
   */
  accountType?: AccountType;
}

const messages = defineMessages({
  create: {
    id: 'sideMenu.newAccount',
    defaultMessage: 'New Account',
  },
  organizations: {
    id: 'accountsMain.organizationsLower',
    defaultMessage: 'organizations',
  },
  individuals: {
    id: 'accountsMain.individualsLower',
    defaultMessage: 'individuals',
  },
});

interface RowData {
  account_id?: string | null;
  account_name?: string | null;
  created_at?: string | null;
  primary_user?: string | null;
  last_login?: string | null;
  num_of_members: number;
}

function AccountList({
  accountType,
  data: accountData,
  parent_id,
}: AccountListProps) {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const data: RowData[] = accountData.map((account) => {
    const { account_id, account_name, created_at, ownerUser, user_count } =
      account;

    return {
      account_id,
      account_name,
      created_at,
      owner_user: formatName(ownerUser?.first_name, ownerUser?.last_name),
      num_of_members: user_count,
    };
  });

  const type = accountData.at(0)?.account_type;

  // Rows matching the currently active search/filter, computed by
  // mui-datatables (see options.onTableChange below). Resynced whenever the
  // underlying tab data changes (tab switch, refetch).
  const [filteredCount, setFilteredCount] = useState(accountData.length);
  useEffect(() => {
    setFilteredCount(accountData.length);
  }, [accountData]);

  const handleCreate = () => {
    navigateRoute(ROUTES.ADMIN_NEW_ACCOUNT, { parent_id });
  };

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _row,
    { dataIndex }
  ) => {
    const rowData = data[dataIndex];
    const { account_id } = rowData;
    navigateRoute(`${ROUTES.ACCOUNTS}${account_id}`);
  };

  return (
    <>
      {accountType !== undefined && (
        <ResultsSummary
          label={formatMessage(
            accountType === AccountType.organization
              ? messages.organizations
              : messages.individuals
          )}
          shown={filteredCount}
          total={data.length}
        />
      )}
      <MuiDataTable
        columns={[
          {
            name: 'account_name',
            className: 'small',
            label: 'Account Name',
            options: {
              searchable: true,
              // display: type === AccountType.organization,
            },
          },
          {
            name: 'num_of_members',
            className: 'small',
            label: '# Of Members',
            options: {
              display: type === AccountType.individual,
            },
          },
          {
            name: 'owner_user',
            label: 'Owner User',
            options: {
              searchable: true,
              sort: true,
              // display: type === AccountType.individual,
            },
          },
          {
            name: 'created_at',
            className: 'date',
            label: 'Created Date',
            options: {
              customBodyRender: (last_login?: string | null) =>
                last_login
                  ? formatDate({ date: last_login, formatter: 'datetime' })
                  : '-',
              filter: true,
              sort: true,
            },
          },
        ]}
        data={data}
        options={{
          sort: true,
          search: true,
          filter: true,
          sortOrder: {
            name: 'created_at',
            direction: 'desc',
          },
          onRowClick: handleRowClick,
          onTableChange: (_action, tableState) => {
            setFilteredCount(tableState.displayData.length);
          },
          customToolbar: () => {
            return (
              <RoundedButton
                autoSize
                iconName="plus"
                margin={0}
                onClick={handleCreate}
              >
                {formatMessage(messages.create)}
              </RoundedButton>
            );
          },
        }}
      />
    </>
  );
}

export default AccountList;
